import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { Tab, Tabs, Box, Skeleton, Typography } from '@mui/material';
import { EventItem, useItems } from '@scorenco/components';
import { Types, useTranslation, Locale, RouteLink } from '@scorenco/core';
import { startOfWeek, endOfWeek, addDays, format } from 'date-fns';
import { fr, de } from 'date-fns/locale';
import { useRouter } from 'next/router';

import { T_KEYS } from '@/translations';

import { pickTextColorBasedOnBgColorSimple } from '../../../utils';
import { TabPanel } from '../../TabPanel';

import {
  GetWeekEventsByTeamsDocument,
  GetWeekEventsByTeamsRankSortDocument,
  GetWeekEventsDocument,
  GetWeekEventsRankSortDocument,
} from './_internal/Widget.query.generated';

const dateToString = (date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

const getDatesOfWeek = (nbWeeks = 0) => {
  const currentDate = addDays(new Date(), nbWeeks * 7);

  return {
    startDate: dateToString(startOfWeek(currentDate, { weekStartsOn: 1 })),
    endDate: dateToString(endOfWeek(currentDate, { weekStartsOn: 1 })),
  };
};

type WeekEventsProps = {
  clubId?: number;
  week: number;
  sort?: string;
};

const WeekEvents = ({ clubId, week, teams, sort }: WeekEventsProps) => {
  const { locale } = useTranslation();
  const router = useRouter();

  const { startDate, endDate } = useMemo(() => getDatesOfWeek(week), [week]);

  const getWeekEvents = useQuery(GetWeekEventsDocument, {
    variables: {
      clubId,
      startDate,
      endDate,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || teams?.length > 0 || sort === 'rank',
    context: {
      role: 'anonymous',
    },
  });
  const getWeekEventsByTeams = useQuery(GetWeekEventsByTeamsDocument, {
    variables: {
      clubId,
      startDate,
      endDate,
      teamIds: teams?.map((team) => {
        return team.team_id;
      }),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || teams?.length === 0 || sort === 'rank',
    context: {
      role: 'anonymous',
    },
  });
  const getWeekEventsRankSort = useQuery(GetWeekEventsRankSortDocument, {
    variables: {
      clubId,
      startDate,
      endDate,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || teams?.length > 0 || sort !== 'rank',
    context: {
      role: 'anonymous',
    },
  });
  const getWeekEventsByTeamsRankSort = useQuery(
    GetWeekEventsByTeamsRankSortDocument,
    {
      variables: {
        clubId,
        startDate,
        endDate,
        teamIds: teams?.map((team) => {
          return team.team_id;
        }),
        cacheTTL: router.query.noCache ? 0 : undefined,
      },
      skip: !clubId || teams?.length === 0 || sort !== 'rank',
      context: {
        role: 'anonymous',
      },
    }
  );

  const events = useItems(
    sort === 'rank'
      ? teams?.length === 0
        ? getWeekEventsRankSort.data
            ?.competitions_event_detail_by_club_id_between_dates_order_by_rank
        : getWeekEventsByTeamsRankSort.data
            ?.competitions_event_detail_by_club_id_between_dates_order_by_rank
      : teams?.length === 0
        ? getWeekEvents.data?.competitions_event_detail_by_club_id_between_dates
        : getWeekEventsByTeams.data
            ?.competitions_event_detail_by_club_id_between_dates,
    5
  );

  const matchsList =
    getWeekEventsByTeams.data
      ?.competitions_event_detail_by_club_id_between_dates;

  if (matchsList?.length === 0) {
    return (
      <Typography
        variant="h5"
        className="no-info"
        sx={{
          width: '100%',
          height: '5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
        }}
      >
        Aucune information actuellement
      </Typography>
    );
  }

  return (
    <>
      {events?.map((event, idx) => {
        if (!event?.id) {
          return (
            <Skeleton
              key={idx}
              className="event-skeleton"
              sx={{ width: '100%', height: '3rem' }}
            />
          );
        }

        const showDateSeparator =
          idx === 0 || event?.date !== events?.[idx - 1]?.date;

        const placeName = event.playing_teams?.[0]?.info?.equipment?.name;

        return (
          <>
            {showDateSeparator && (
              <Box
                className="date-separator"
                css={(theme) => css`
                  width: 100%;
                  text-align: center;
                  background-color: ${theme.vars.palette.primary.main};
                  color: ${pickTextColorBasedOnBgColorSimple(
                    theme.vars.palette.primary.main,
                    '#fff',
                    '#000'
                  )};
                  border-top: ${idx > 0 && '1px solid'};
                  border-bottom: 1px solid;
                  border-color: ${theme.vars.palette.divider};
                  padding: 0.3rem 0;
                  font-size: 0.8rem;
                  margin-top: ${idx > 0 && '0.8rem'};
                  &:first-letter {
                    text-transform: uppercase;
                  }
                `}
              >
                {format(new Date(event.date), 'EEEE d MMMM', {
                  locale: locale === Locale.de_CH ? de : fr,
                })}
              </Box>
            )}
            <RouteLink
              href={event.url && process.env.NEXT_PUBLIC_WEB_URL + event.url}
            >
              <a
                className="event-anchor"
                target="_blank"
                style={{ textDecoration: 'none', color: 'unset' }}
              >
                <EventItem
                  key={event.id}
                  className="event-item"
                  event={event}
                  clubId={clubId}
                  showShortName={true}
                  forcedPlace={placeName}
                />
              </a>
            </RouteLink>
          </>
        );
      })}
    </>
  );
};

type WeekEventsWidgetProps = {
  clubId?: number;
  teams?: Types.Widgets_Widget_Has_Team[];
  sort?: string;
  daySwitch?: string;
};

export const WeekEventsWidget = ({
  teams,
  sort,
  clubId,
  daySwitch,
}: WeekEventsWidgetProps) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(1);

  useEffect(() => {
    const today = new Date().getDay();

    switch (daySwitch) {
      case 'never':
        setCurrentTab(0);
        break;
      case 'now':
        setCurrentTab(1);
        break;
      case 'monday':
        setCurrentTab(1 <= today ? 1 : 0);
        break;
      case 'tuesday':
        setCurrentTab(2 <= today ? 1 : 0);
        break;
      case 'wednesday':
        setCurrentTab(3 <= today ? 1 : 0);
        break;
      case 'thursday':
        setCurrentTab(4 <= today ? 1 : 0);
        break;
      case 'friday':
        setCurrentTab(5 <= today ? 1 : 0);
        break;
      case 'saturday':
        setCurrentTab(6 <= today ? 1 : 0);
        break;
      case 'sunday':
        setCurrentTab(7 <= today ? 1 : 0);
        break;
      default:
        setCurrentTab(1);
    }
  }, [daySwitch]);

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Box
      className="week-events-widget"
      sx={{ width: '100%', marginBottom: '1.5rem' }}
    >
      <Box
        className="tab-container"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0.5 }}
      >
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          centered
          variant="fullWidth"
          className="tabs"
        >
          <Tab label={t(T_KEYS.PREVIOUS)} className="tab-previous" />
          <Tab label={t(T_KEYS.CURRENT)} className="tab-current" />
          <Tab label={t(T_KEYS.NEXT)} className="tab-next" />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0} className="tab-panel-previous">
        <WeekEvents clubId={clubId} week={-1} teams={teams} sort={sort} />
      </TabPanel>
      <TabPanel value={currentTab} index={1} className="tab-panel-current">
        <WeekEvents clubId={clubId} week={0} teams={teams} sort={sort} />
      </TabPanel>
      <TabPanel value={currentTab} index={2} className="tab-panel-next">
        <WeekEvents clubId={clubId} week={1} teams={teams} sort={sort} />
      </TabPanel>
    </Box>
  );
};
